/**
 * Phase 20 — Viral Attribution Admin Dashboard
 * =============================================
 * Surfaces the funnel of "verdict_pdf_shared" → "signup_attributed_to_share"
 * events. Read-only view over the existing analytics_events collection.
 *
 * Three sections:
 *   1. Top KPIs (4 tiles): total shares, last-30d, signups attributed, rate
 *   2. Daily breakdown chart (Recharts area — shares & attributed signups)
 *   3. Top sharers + Top shared deals tables
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend,
} from "recharts";
import { TrendingUp, Share2, UserPlus, Percent, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import api from "@/lib/api";
import { toast } from "sonner";

function isoDate(daysAgo = 0) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function KpiTile({ label, value, sub, Icon, tone = "neutral" }) {
  const toneClass = {
    positive: "border-emerald-200 bg-emerald-50",
    warning: "border-amber-200 bg-amber-50",
    neutral: "border-slate-200 bg-white",
  }[tone];
  return (
    <Card className={`p-5 ${toneClass} shadow-sm`} data-testid={`viral-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon ? <Icon className="h-4 w-4 text-slate-400" /> : null}
      </div>
      <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
      {sub ? <p className="mt-1 text-xs text-slate-600">{sub}</p> : null}
    </Card>
  );
}

export default function AdminViralAttribution() {
  const [from, setFrom] = useState(isoDate(30));
  const [to, setTo] = useState(isoDate(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/admin/viral-attribution", {
        params: { from, to },
      });
      setData(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load attribution data");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); /* refetch when dates change */ }, [load]);

  const summary = data?.summary || {};
  const topSharers = data?.top_sharers || [];
  const topDeals = data?.top_shared_deals || [];

  // For an empty state with no daily events, still render the chart with
  // zero-filled days so the visualization isn't a flat baseline-of-nothing.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `data` covers `daily`
  const chartData = useMemo(() => {
    const daily = data?.daily_breakdown || [];
    if (daily.length > 0) return daily;
    return [
      { date: from, shares: 0, attributed_signups: 0 },
      { date: to, shares: 0, attributed_signups: 0 },
    ];
  }, [data, from, to]);

  return (
    <div className="space-y-6 p-6" data-testid="admin-viral-attribution">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Viral Attribution</h1>
          <p className="mt-1 text-sm text-slate-600">
            PDF-share → signup funnel. Powered by <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">analytics_events</code>.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label htmlFor="va-from" className="text-xs text-slate-600">From</Label>
            <Input id="va-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-36" data-testid="viral-filter-from" />
          </div>
          <div>
            <Label htmlFor="va-to" className="text-xs text-slate-600">To</Label>
            <Input id="va-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-36" data-testid="viral-filter-to" />
          </div>
          <Button onClick={load} disabled={loading} size="sm" className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="viral-refresh">
            {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiTile
          label="Total Shares"
          value={summary.pdfs_shared_total ?? 0}
          sub={`${summary.unique_sharers ?? 0} unique sharers · avg ${summary.avg_shares_per_user ?? 0}`}
          Icon={Share2}
        />
        <KpiTile
          label="Shares 30d"
          value={summary.pdfs_shared_last_30d ?? 0}
          sub="rolling window"
          Icon={TrendingUp}
        />
        <KpiTile
          label="Signups Attributed"
          value={summary.signups_attributed ?? 0}
          sub="all-time (utm_source=shared_pdf)"
          tone={summary.signups_attributed > 0 ? "positive" : "neutral"}
          Icon={UserPlus}
        />
        <KpiTile
          label="Attribution Rate"
          value={`${(summary.attribution_rate_pct ?? 0).toFixed(1)}%`}
          sub="signups / shares"
          tone={
            (summary.attribution_rate_pct ?? 0) >= 5 ? "positive"
            : (summary.attribution_rate_pct ?? 0) >= 1 ? "warning" : "neutral"
          }
          Icon={Percent}
        />
      </div>

      <Card className="border-slate-200 p-5 shadow-sm" data-testid="viral-chart-card">
        <p className="text-sm font-semibold text-slate-900">Daily funnel</p>
        <p className="mt-0.5 text-xs text-slate-500">Shares vs. attributed signups, {from} → {to}.</p>
        <div className="mt-4 h-72 w-full">
          <ResponsiveContainer>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="vaShares" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#4f46e5" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="vaSignups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tickLine={false} axisLine={false} stroke="#94a3b8" fontSize={11} />
              <YAxis tickLine={false} axisLine={false} stroke="#94a3b8" fontSize={11} allowDecimals={false} width={36} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
              <Area type="monotone" dataKey="shares" stroke="#4f46e5" strokeWidth={2} fill="url(#vaShares)" name="Shares" />
              <Area type="monotone" dataKey="attributed_signups" stroke="#059669" strokeWidth={2} fill="url(#vaSignups)" name="Signups (attributed)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 p-5 shadow-sm" data-testid="viral-top-sharers">
          <p className="text-sm font-semibold text-slate-900">Top sharers</p>
          <p className="mt-0.5 text-xs text-slate-500">Power users to thank / interview.</p>
          <Table className="mt-3">
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead className="text-right">Shares</TableHead>
                <TableHead className="text-right">Signups</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topSharers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-sm text-slate-500">No shares yet.</TableCell>
                </TableRow>
              ) : topSharers.map((s) => (
                <TableRow key={s.user_id} data-testid={`viral-sharer-${s.user_id}`}>
                  <TableCell className="text-slate-900">
                    {s.name || "—"}
                    <div className="text-xs text-slate-500">{s.email}</div>
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{s.shares}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.signups_attributed}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="border-slate-200 p-5 shadow-sm" data-testid="viral-top-deals">
          <p className="text-sm font-semibold text-slate-900">Most-shared deals</p>
          <p className="mt-0.5 text-xs text-slate-500">Which property write-ups are landing.</p>
          <Table className="mt-3">
            <TableHeader>
              <TableRow>
                <TableHead>Deal</TableHead>
                <TableHead className="text-right">Shares</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topDeals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-sm text-slate-500">No shared deals yet.</TableCell>
                </TableRow>
              ) : topDeals.map((d) => (
                <TableRow key={d.deal_id} data-testid={`viral-deal-${d.deal_id}`}>
                  <TableCell className="text-slate-900">
                    {d.address || "—"}
                    {d.city ? <div className="text-xs text-slate-500">{d.city}{d.state ? `, ${d.state}` : ""}</div> : null}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{d.shares}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
